import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MailLink({ email, phone, testid }) {
  if (!email) return <span className="text-slate-300" data-testid={testid}>call Brandon Rinker Jr. at {phone || "the program number"} or reply HELP to any text from Brandon Rinker Jr.</span>;
  return <span data-testid={testid}>email <a href={`mailto:${email}`} className="text-orange-400 hover:text-orange-300 underline">{email}</a></span>;
}

export default function Terms() {
  const [contacts, setContacts] = useState({ privacy_email: null, support_email: null, help_phone: null });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/legal-contacts`).then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <nav className="border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="terms-nav-home">← Back to home</Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="terms-page">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Legal</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">Terms of Service</h1>
        <p className="text-slate-500 text-sm mb-10" data-testid="terms-updated">Last updated: June 2026</p>

        <div className="space-y-6 text-slate-300 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">1. Acceptance of these terms</h2>
            <p>
              These Terms of Service ("Terms") govern your use of RoofLead and your interactions with us by phone, SMS,
              or web. By submitting an inquiry, opting in to SMS, or otherwise using the service, you agree to these Terms
              and to our <Link to="/privacy" className="text-orange-400 hover:text-orange-300 underline">Privacy Policy</Link>.
              If you do not agree, please do not use the service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">2. About RoofLead</h2>
            <p>
              RoofLead is a software and service platform that helps roofing businesses respond to incoming leads and
              manage customer conversations. RoofLead is <span className="text-white font-semibold">not</span> a roofing
              contractor and does not perform roof inspections, roofing work, or any other construction services. All
              on-site services, pricing, and warranties are provided by independent roofing businesses that use the
              RoofLead platform.
            </p>
            <p className="mt-3 bg-slate-800/60 border border-slate-700 rounded-lg p-4" data-testid="terms-testing-program">
              <span className="text-white font-semibold">RoofLead platform development &amp; testing program.</span> Text
              messages and the consent phone line for this program are sent and operated by{" "}
              <span className="text-white font-semibold" data-testid="terms-sms-sender">Brandon Rinker Jr.</span>, the registered
              SMS sender, as part of the RoofLead software development and testing program. RoofLead is only the name of the
              software product/platform. No roofing services, inspections, estimates, or roofing work are offered.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">3. Eligibility</h2>
            <p>You must be at least 18 years old and the authorized user of the mobile phone number you provide.</p>
          </section>

          <section id="sms">
            <h2 className="font-display text-2xl font-bold text-white mb-3">4. SMS Terms</h2>
            <p>
              <span className="text-white font-semibold">Brandon Rinker Jr.</span> is the SMS sender for the{" "}
              <span className="text-white font-semibold">RoofLead software development &amp; testing program</span>; RoofLead is only the software product/platform.
              Users who opt in, either by pressing 1 on their phone keypad after hearing the Brandon Rinker Jr. consent message when a
              call is missed (see <Link to="/sms-consent" className="text-orange-400 hover:text-orange-300 underline">/sms-consent</Link>)
              or on the web form at /get-quote, may receive text messages from Brandon Rinker Jr. following up on their call or inquiry.
              Neither Brandon Rinker Jr. nor RoofLead provides roofing services under this program. Callers who do not press 1 receive no text
              messages and may leave a voicemail for a callback instead.
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-3" data-testid="terms-sms-section">
              <li><span className="text-white font-semibold">Message frequency varies</span> based on your inquiry activity.</li>
              <li><span className="text-white font-semibold">Message and data rates may apply</span> from your mobile carrier.</li>
              <li><span className="text-white font-semibold">Reply STOP</span> to any message to opt out at any time. You will receive one confirmation reply and then no further texts.</li>
              <li><span className="text-white font-semibold">Reply HELP</span> for assistance, or <MailLink phone={contacts.help_phone} email={contacts.support_email} testid="terms-support-email" />.</li>
              <li><span className="text-white font-semibold">Consent is not a condition</span> of submitting an inquiry or purchasing any service.</li>
              <li><span className="text-white font-semibold">Supported carriers:</span> AT&amp;T, T-Mobile, Verizon, and other major US carriers. Carriers are not liable for delayed or undelivered messages.</li>
              <li><span className="text-white font-semibold">No marketing SMS.</span> This program only sends service-related messages tied to your inquiry.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">5. Accuracy of information</h2>
            <p>
              You represent and warrant that the phone number and contact information you provide is your own or that
              you are otherwise legally authorized to provide it and to receive messages at that number. You agree to
              notify us promptly if you change or discontinue the number.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">6. AI-assisted messages</h2>
            <p>
              Some replies you receive may be drafted with the help of artificial intelligence. You should not rely on
              any single SMS as a binding quote or contract — pricing, scheduling, and scope of work are confirmed in
              writing by the roofing business handling your inquiry.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">7. No guarantees</h2>
            <p>
              An inquiry submitted through RoofLead is not a binding contract for services with any specific roofing
              business. RoofLead does not warrant or guarantee the work, pricing, or availability of roofing businesses
              that use the platform. The RoofLead service is provided "as is" without warranties of any kind, whether
              express or implied.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">8. Prohibited use</h2>
            <p>
              You may not use RoofLead to submit false, misleading, or spam inquiries, to submit phone numbers you do not
              own, or to interfere with the operation of the platform.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">9. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, RoofLead shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages arising out of your use of the service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">10. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. Continued use after a change constitutes acceptance of the
              revised Terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">11. Contact</h2>
            <p>
              Questions? You can <MailLink phone={contacts.help_phone} email={contacts.support_email} testid="terms-contact-email" />.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-wrap gap-4 text-sm">
          <Link to="/privacy" className="text-orange-400 hover:text-orange-300" data-testid="terms-privacy-link">Privacy Policy →</Link>
          <Link to="/get-quote" className="text-orange-400 hover:text-orange-300" data-testid="terms-quote-link">Contact RoofLead →</Link>
        </div>
      </article>
    </div>
  );
}
